/**
 * ServiceDetailPage — full page view of one service.
 *
 * Route: `/services/:serviceId` (ProtectedRoute).
 *
 * Reached from:
 *   - ServicesPage card tap (most common — cache is already warm)
 *   - Direct URL / bookmark / shared link (cold cache; triggers fetch)
 *
 * Data strategy:
 *   We share the cache with ServicesPage by reusing the same query key
 *   `['services']`. TanStack Query will:
 *     a) serve instantly from cache when the user comes from the list
 *     b) trigger the same fetch when the cache is cold (direct URL load)
 *     c) dedupe with the list page's query if both pages mount at once
 *
 *   The service we want is looked up by id from the full list. If the
 *   id is invalid (typo, deleted service), we show a "not found" state
 *   rather than rendering with undefined data.
 *
 * Why no per-service GET endpoint:
 *   BACKEND_CONTRACT.md doesn't currently expose `/services/:id`; only
 *   `/services` (list) and `POST /services/:id/checkout`. Since the list
 *   response includes everything the detail page needs, we don't need a
 *   second endpoint. If a per-service endpoint appears later, switch to
 *   `useQuery({ queryKey: ['services', serviceId], ... })`.
 *
 * Request flow (out of scope for Session 7):
 *   The bottom "Request this service" CTA calls `servicesApi.checkoutService`
 *   to get a proforma + payment URL, then routes the user to the payment
 *   gateway. That's Session 8 work. For now the button is disabled with
 *   a "Coming soon" helper text — same pattern we used for the dashboard
 *   nav cards before they were wired up.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Stack,
  Typography,
  Card,
  CardContent,
  Chip,
  Box,
  IconButton,
  Skeleton,
  Alert,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import RefreshIcon from '@mui/icons-material/Refresh';

import LanguageToggle from '@/components/LanguageToggle';
import CartBar from '@/components/CartBar';
import { servicesApi, consultationsApi } from '@/services/api';
import { selectLanguage, selectIsRtl } from '@/store/uiSlice';
import { addItem, selectIsInCart } from '@/store/cartSlice';
import { formatToman } from '@/utils/currency';
import { localized } from '@/utils/localizedContent';

/**
 * Translate a category code to its localized label. Shared with
 * ServicesPage's helper of the same name (could be extracted to a util
 * if a third caller appears — see tech debt note in CLAUDE.md).
 */
function getCategoryLabel(code, t) {
  const key = `services.category.${code}`;
  const translated = t(key);
  return translated === key ? code : translated;
}

export function ServiceDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { serviceId } = useParams();
  const language = useSelector(selectLanguage);
  const isRtl = useSelector(selectIsRtl);
  const inCart = useSelector(selectIsInCart(serviceId));
  const [addedOpen, setAddedOpen] = useState(false);
  const [startError, setStartError] = useState('');

  // Consultation services begin with a free capture. Nothing is charged here;
  // the client pays later, once they have usable photos and a doctor.
  const startMut = useMutation({
    mutationFn: () => consultationsApi.startConsultation(serviceId),
    onSuccess: (c) => {
      navigate(`/face-capture?consultation=${encodeURIComponent(c.id)}`);
    },
    onError: (err) =>
      setStartError(err?.response?.data?.error?.message || t('common.error')),
  });

  // Same query key as ServicesPage → cache reuse, no second fetch when
  // arriving from the list. The full list comes back; we pluck the one
  // we want below.
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['services'],
    queryFn: () => servicesApi.listServices({ pageSize: 50 }),
    staleTime: 5 * 60 * 1000,
  });

  const service = (data?.data?.services ?? []).find((s) => s.id === serviceId);

  // Three possible states once loading completes:
  //   1) isError                 → network/server problem (Retry)
  //   2) !isError && !service    → id not in list (Not Found)
  //   3) service present         → render detail
  // While loading we show a skeleton regardless of which it'll be.

  const handleBack = () => {
    // Use navigate(-1) so the user returns to whatever filter/scroll
    // position they had on the list. Fallback to /services if there's
    // no history (direct URL entry).
    if (window.history.length > 1) navigate(-1);
    else navigate('/services');
  };

  const handleAddToCart = () => {
    if (!service) return;
    // Add (idempotent) then offer: keep shopping, or go to the cart to pay.
    dispatch(
      addItem({ id: service.id, name: service.name, priceToman: service.priceToman }),
    );
    setAddedOpen(true);
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={handleBack}
              aria-label={t('common.back')}
            >
              {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>
            <MedicalServicesIcon color="primary" />
            <Typography variant="h6" component="h1" fontWeight={700}>
              {t('services.detail.title')}
            </Typography>
          </Stack>
          <LanguageToggle />
        </Stack>

        {/* ---- Loading ---- */}
        {isLoading && (
          <Stack spacing={2}>
            <Skeleton variant="text" width="70%" height={36} />
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
          </Stack>
        )}

        {/* ---- Network/server error ---- */}
        {isError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                disabled={isFetching}
              >
                {t('common.retry')}
              </Button>
            }
          >
            {t('services.error.loadFailed')}
          </Alert>
        )}

        {/* ---- Service not found ---- */}
        {!isLoading && !isError && !service && (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <InfoOutlinedIcon sx={{ fontSize: 48, opacity: 0.5 }} />
            <Typography variant="h6" textAlign="center">
              {t('services.detail.notFound.title')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              {t('services.detail.notFound.message')}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/services')}
              startIcon={isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            >
              {t('services.detail.notFound.backToList')}
            </Button>
          </Stack>
        )}

        {/* ---- Service detail ---- */}
        {!isLoading && !isError && service && (() => {
          // Resolve bilingual content once. The gates below check the
          // localized string (not the object) so empty {fa:'',en:''}
          // correctly hides the description / preparation blocks.
          const name = localized(service.name, language);
          const description = localized(service.description, language);
          const preparation = localized(service.preparation, language);
          return (
          <Stack spacing={3}>
            {/* Hero block: name + category */}
            <Box>
              <Typography variant="h5" component="h2" fontWeight={700} gutterBottom>
                {name}
              </Typography>
              <Chip
                size="small"
                label={getCategoryLabel(service.category, t)}
                variant="outlined"
              />
            </Box>

            {/* Price card — visually prominent because it's what the user
                wants to know most before committing */}
            <Card variant="outlined" sx={{ bgcolor: 'primary.50' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  {t('services.detail.priceLabel')}
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ mt: 0.5 }}
                >
                  {formatToman(service.priceToman, language)}
                </Typography>
              </CardContent>
            </Card>

            {/* Description — only shown if non-empty */}
            {description && (
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  {t('services.detail.descriptionLabel')}
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                  {description}
                </Typography>
              </Box>
            )}

            <Divider />

            {/* Duration row */}
            {service.durationMinutes != null && (
              <Stack direction="row" spacing={2} alignItems="center">
                <AccessTimeIcon color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('services.detail.durationLabel')}
                  </Typography>
                  <Typography variant="body2">
                    {t('services.card.durationMinutes', {
                      count: service.durationMinutes,
                    })}
                  </Typography>
                </Box>
              </Stack>
            )}

            {/* Preparation callout — only if the service has prep
                instructions. This is medically important (e.g. "fast for
                8 hours") so it's styled as an informational Alert, not
                buried in body text. */}
            {preparation && (
              <Alert
                severity="info"
                icon={<InfoOutlinedIcon />}
                sx={{ alignItems: 'flex-start' }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  {t('services.detail.preparationLabel')}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {preparation}
                </Typography>
              </Alert>
            )}

            {/* A consultation service starts with a FREE capture, not the
                cart: the client photographs first and only pays once they have
                usable photos and a doctor. Everything else still goes through
                the normal cart/checkout handoff. */}
            <Box sx={{ pt: 1 }}>
              {service?.requiresPhysician ? (
                <Stack spacing={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<PhotoCameraIcon />}
                    disabled={startMut.isPending}
                    onClick={() => startMut.mutate()}
                  >
                    {startMut.isPending
                      ? t('common.loading')
                      : t('services.detail.startFree')}
                  </Button>
                  <Typography variant="caption" color="text.secondary" align="center">
                    {t('services.detail.startFreeHint')}
                  </Typography>
                  {startError && <Alert severity="error">{startError}</Alert>}
                </Stack>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={<AddShoppingCartIcon />}
                  onClick={handleAddToCart}
                >
                  {inCart ? t('cart.inCart') : t('cart.addToCart')}
                </Button>
              )}
            </Box>
          </Stack>
          );
        })()}
      </Stack>

      <CartBar />

      {/* After adding: keep shopping or go to the cart to pay. */}
      <Dialog open={addedOpen} onClose={() => setAddedOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('cart.added.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('cart.added.body')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={() => setAddedOpen(false)}>{t('cart.added.continue')}</Button>
          <Button variant="contained" onClick={() => navigate('/cart')}>
            {t('cart.added.goToCart')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ServiceDetailPage;
